import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PREFERRED_TERMS } from "../../lib/constants";
import { PRONOUNS, PRONOUN_VISIBILITY } from "../../lib/locale";
import { getBrand, pubFromHostname } from "../../lib/brand";
import CountrySelect from "../../components/CountrySelect";
import PublicHeader from "../../components/PublicHeader";
import SiteFooter from "../../components/SiteFooter";
import CoeditorGate from "../../components/CoeditorGate";
import { api, formatApiError, setSubmissionAccessToken } from "../../lib/api";
import { toast } from "sonner";
import { PlusCircle, X, ArrowRight } from "lucide-react";

const emptyLover = () => ({
  first_name: "",
  last_name: "",
  instagram: "",
  ok_to_tag: false,
  preferred_term: "",
  pronouns: "",
  pronoun_other: "",
  pronoun_visibility: "",
  identity: "",
});

const PARTNER_LABELS = ["One", "Two", "Three", "Four", "Five"];

export default function CreateEvent() {
  const navigate = useNavigate();
  const { id } = useParams(); // present on /create-event/:id/edit
  const [params] = useSearchParams();
  const [pub, setPub] = useState(null); // null = neutral until chosen/known
  const [pubLocked, setPubLocked] = useState(false); // locked after Step 1 for lovers
  const [eventName, setEventName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [sendNotification, setSendNotification] = useState(false);
  const [eventCountry, setEventCountry] = useState("");
  const [eventCity, setEventCity] = useState("");
  const [storyCategory, setStoryCategory] = useState("");
  const [storyTypes, setStoryTypes] = useState([]);
  const [galleryLink, setGalleryLink] = useState("");
  const [galleryPassword, setGalleryPassword] = useState("");
  const [partners, setPartners] = useState([emptyLover(), emptyLover()]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [brand, setBrand] = useState({});

  // Default publication: ?pub= param wins, then per-domain Settings mapping, else neutral
  useEffect(() => {
    (async () => {
      const data = await getBrand();
      setBrand(data);
      const qp = params.get("pub");
      if (qp === "dwh" || qp === "dwt") { setPub(qp); return; }
      const hostPub = pubFromHostname(data);
      if (hostPub) setPub(hostPub);
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/meta/story-categories");
        setStoryTypes((data.categories || []).filter((c) => c.key !== "styled_shoot"));
      } catch { /* noop */ }
    })();
  }, []);

  // Edit mode: prefill from the saved draft
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/events/${id}/draft`);
        setPub(data.publication === "either" ? "dwh" : data.publication);
        setPubLocked(data.submitter_type !== "pro");
        setEventName(data.event_name || "");
        setClientEmail(data.client_email || "");
        setSendNotification(!!data.send_notification);
        setEventCountry(data.event_country || "");
        setEventCity(data.event_city || "");
        setStoryCategory(data.story_category || "");
        setGalleryLink(data.gallery_link || "");
        if ((data.partners || []).length >= 2) {
          setPartners(data.partners.map((p) => ({
            ...emptyLover(), ...p,
            pronoun_other: "",
            ok_to_tag: !!p.ok_to_tag,
          })));
        }
      } catch {
        toast.error("Could not load this submission.");
      }
    })();
  }, [id]);

  useEffect(() => {
    if (pub) document.documentElement.dataset.pub = pub;
    return () => { delete document.documentElement.dataset.pub; };
  }, [pub]);

  const update = (i, f, v) => setPartners((a) => a.map((p, idx) => idx === i ? { ...p, [f]: v } : p));
  const addPartner = () => setPartners((a) => a.length < 5 ? [...a, emptyLover()] : a);
  const removePartner = (i) => setPartners((a) => a.length > 2 ? a.filter((_, idx) => idx !== i) : a);

  const pubLogo = (code) => brand[`${code}_logo_url`] || "";

  const pubButton = (code, label) => (
    <button
      type="button"
      onClick={() => !pubLocked && setPub(code)}
      disabled={pubLocked}
      data-testid={`pub-select-${code}`}
      className={`pub-btn pub-btn-${code} ${pub === code ? "active" : ""} ${pubLocked ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      {pubLogo(code) ? (
        <img
          src={pubLogo(code)}
          alt={label}
          className={`h-8 w-auto object-contain ${code === "dwt" && pub === "dwt" ? "brightness-0 invert" : ""}`}
        />
      ) : (
        <span
          className="pub-label text-sm font-bold uppercase tracking-widest"
          style={{ color: code === "dwt" && pub === "dwt" ? "#fff" : "#000" }}
        >
          {label}
        </span>
      )}
    </button>
  );

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    const fail = (message) => { setFormError(message); toast.error(message); };
    if (!pub) return fail("Please choose a publication.");
    if (!eventName.trim()) return fail("Please name your story.");
    if (!clientEmail.trim()) return fail("Please add a primary contact email.");
    if (!storyCategory) return fail("Please choose your story type.");
    const valid = partners.filter((p) => p.first_name.trim());
    if (valid.length < 2) return fail("Please add at least 2 lovers with a first name.");
    setSubmitting(true);
    const payload = {
      publication: pub,
      event_name: eventName.trim(),
      client_email: clientEmail.trim(),
      send_notification: sendNotification,
      event_country: eventCountry,
      event_city: eventCity.trim(),
      story_category: storyCategory,
      gallery_link: galleryLink.trim(),
      gallery_password: galleryPassword,
      partners: valid,
    };
    try {
      if (id) {
        await api.put(`/events/${id}/details`, payload);
        navigate(`/create-event/${id}/vendors`);
      } else {
        const { data } = await api.post("/create-event", payload);
        setSubmissionAccessToken(data.event_id, data.access_token);
        toast.success("Draft started — continue through vendor credits, your story, and final consent to submit it to our team.");
        navigate(`/create-event/${data.event_id}/vendors`);
      }
    } catch (err) {
      const message = formatApiError(err, "We could not save your submission draft.");
      setFormError(message);
      toast.error(message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f5f3ed] flex flex-col" data-testid="create-event-page" data-pub={pub || "neutral"}>
      <PublicHeader pub={pub} />

      <CoeditorGate eventId={id || "new"}>
      <main className="w-full max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-16">
      <form onSubmit={submit}>
        {formError && <div role="alert" className="mb-6 border border-[#b35c5c] bg-white p-4 text-sm text-[#9b3f3f]" data-testid="create-event-error">{formError}</div>}
        {/* PUBLICATION */}
        <div className="mb-10" data-testid="section-publication">
          <p className="btw-step-label">Select a publication</p>
          <h1 className="btw-step-desc" data-testid="publication-heading">
            Where would you prefer your story to be published?{pubLocked ? " (Locked after this step — our team can change it for you.)" : ""}
          </h1>
          <div className="grid sm:grid-cols-2 gap-3">
            {pubButton("dwh", "Dancing With Her")}
            {pubButton("dwt", "Dancing With Them")}
            {pubButton("either", "Consider for either publication")}
          </div>
        </div>

        {/* WHAT BEST DESCRIBES YOU */}
        {!id && (
          <div className="mb-10" data-testid="section-iam">
            <p className="btw-step-label">What best describes you</p>
            <p className="btw-step-desc">I am….</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="pub-btn pub-btn-dwh active pointer-events-none" data-testid="iam-lovers">
                <span className="pub-label text-sm font-bold text-black">A lover featured in the story</span>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/pro/new${pub ? `?pub=${pub}` : ""}`)}
                data-testid="iam-pro"
                className="pub-btn"
              >
                <span className="pub-label text-sm font-bold text-black">A wedding vendor submitting a story</span>
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 */}
        <div className="mb-10" data-testid="section-event">
          <p className="btw-step-label">Step 1 - Your details</p>
          <p className="btw-step-desc">So that we can get in touch when we share your love story.</p>
          <div className="btw-section space-y-5">
            <div>
              <label className="text-xs font-bold block mb-1">The name of your event</label>
              <input data-testid="event-name-input" className="btw-input" placeholder="Casey &amp; Riley" value={eventName} onChange={(e) => setEventName(e.target.value)}  aria-label="The name of your event"/>
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Story Type</label>
              <div className="btw-select-wrap">
                <select data-testid="story-type-select" className="btw-input btw-input-select bg-transparent appearance-none" value={storyCategory} onChange={(e) => setStoryCategory(e.target.value)} aria-label="Story Type">
                  <option value="">Select</option>
                  {storyTypes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Contact Email</label>
              <input data-testid="client-email-input" type="email" className="btw-input" placeholder="you@yourlove.com" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}  aria-label="Contact Email"/>
            </div>
            <label className="flex items-center gap-2 text-sm text-black cursor-pointer">
              <input type="checkbox" data-testid="send-notification-checkbox" className="btw-check" checked={sendNotification} onChange={(e) => setSendNotification(e.target.checked)} />
              Send me a confirmation email when I submit.
            </label>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold block mb-1">Country of the event</label>
                <CountrySelect testid="event-country" value={eventCountry} onChange={setEventCountry} placeholder="Where it took place" />
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">City, State</label>
                <input data-testid="event-city-input" className="btw-input" placeholder="eg. Brooklyn, NY" value={eventCity} onChange={(e) => setEventCity(e.target.value)}  aria-label="City, State"/>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-bold block mb-1">Gallery Link</label>
                <input data-testid="gallery-link-input" type="url" className="btw-input" placeholder="https://…" value={galleryLink} onChange={(e) => setGalleryLink(e.target.value)}  aria-label="Gallery Link"/>
              </div>
              <div>
                <label className="text-xs font-bold block mb-1">Gallery Password (Optional)</label>
                <input data-testid="gallery-password-input" className="btw-input" placeholder={id ? "Leave blank to keep unchanged" : "If Gallery is Locked"} value={galleryPassword} onChange={(e) => setGalleryPassword(e.target.value)}  aria-label="Gallery Password (Optional)"/>
              </div>
            </div>
          </div>
        </div>

        {/* PARTNERS */}
        <div className="mb-10" data-testid="section-partners">
          <p className="btw-step-desc">Add a block for each person. This is how we'll refer to you in our content so choose language that feels right.</p>
          {partners.map((p, i) => (
            <div key={i} className="btw-section" data-testid={`partner-block-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider">Partner {PARTNER_LABELS[i]}</span>
                {partners.length > 2 && (
                  <button type="button" aria-label={`Remove partner ${i + 1}`} onClick={() => removePartner(i)} data-testid={`remove-partner-${i}`} className="text-black hover:text-[#b35c5c]">
                    <X size={16} />
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label className="text-xs font-bold block mb-1">Partner {PARTNER_LABELS[i]} - Name</label>
                  <input data-testid={`partner-first-name-${i}`} aria-label={`Partner ${PARTNER_LABELS[i]} name`} className="btw-input" placeholder="First Name" value={p.first_name} onChange={(e) => update(i, "first_name", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-bold block mb-1">Partner {PARTNER_LABELS[i]} - Pronouns</label>
                  <div className="btw-select-wrap">
                    <select data-testid={`partner-pronouns-${i}`} aria-label={`Partner ${PARTNER_LABELS[i]} pronouns`} className="btw-input btw-input-select bg-transparent appearance-none" value={p.pronouns} onChange={(e) => update(i, "pronouns", e.target.value)}>
                      <option value="">Select</option>
                      {PRONOUNS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  {p.pronouns === "Other" && (
                    <input data-testid={`partner-pronouns-other-${i}`} placeholder="Your pronouns" value={p.pronoun_other} onChange={(e) => update(i, "pronoun_other", e.target.value)} className="btw-input mt-3" />
                  )}
                </div>
                <div>
                  <label className="text-xs font-bold block mb-1">Instagram</label>
                  <div className="btw-ig">
                    <span className="text-black">@</span>
                    <input data-testid={`partner-instagram-${i}`} aria-label={`Partner ${PARTNER_LABELS[i]} Instagram`} className="btw-input pl-1" value={p.instagram} onChange={(e) => update(i, "instagram", e.target.value.replace(/^@/, ""))} />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-black cursor-pointer mt-2">
                    <input type="checkbox" data-testid={`partner-ok-tag-${i}`} className="btw-check" checked={!!p.ok_to_tag} onChange={(e) => update(i, "ok_to_tag", e.target.checked)} />
                    Is it okay to tag you on Instagram?
                  </label>
                </div>
                <div>
                  <label className="text-xs font-bold block mb-1">How can we identify you?</label>
                  <div className="btw-select-wrap">
                    <select
                      data-testid={`partner-term-${i}`}
                      aria-label={`Partner ${PARTNER_LABELS[i]} - how can we identify you`}
                      className="btw-input btw-input-select bg-transparent appearance-none"
                      value={p.preferred_term && !PREFERRED_TERMS.includes(p.preferred_term) ? "__other__" : p.preferred_term}
                      onChange={(e) => {
                        if (e.target.value === "__other__") update(i, "preferred_term", " ");
                        else update(i, "preferred_term", e.target.value);
                      }}
                     aria-label="How can we identify you?">
                      <option value="">Select</option>
                      {PREFERRED_TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
                      <option value="__other__">Other (write your own)</option>
                    </select>
                  </div>
                  {(p.preferred_term && !PREFERRED_TERMS.includes(p.preferred_term)) && (
                    <input data-testid={`partner-term-other-${i}`} placeholder="Your own term" value={p.preferred_term.trim()} onChange={(e) => update(i, "preferred_term", e.target.value || " ")} className="btw-input mt-3" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold block mb-2">How can we use your pronouns?</label>
                  <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
                    {PRONOUN_VISIBILITY.map((opt) => (
                      <label key={opt.value} className="flex items-center gap-2 text-sm text-black cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`partner-pron-vis-${i}-${opt.value}`}
                          className="btw-check"
                          checked={p.pronoun_visibility === opt.value}
                          onChange={() => update(i, "pronoun_visibility", p.pronoun_visibility === opt.value ? "" : opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            {partners.length < 5 ? (
              <button type="button" onClick={addPartner} data-testid="add-partner-button" className="btw-btn-outline w-full sm:w-auto">
                <PlusCircle size={15} /> Add a partner
              </button>
            ) : <span />}
            <button type="submit" disabled={submitting} data-testid="create-event-submit" className="btw-btn w-full sm:w-auto">
              <span>{submitting ? "Saving…" : "Continue to vendor credits"}</span>
              <span className="btw-btn-arrow"><ArrowRight size={12} /></span>
            </button>
          </div>
        </div>
      </form>
      </main>
      </CoeditorGate>

      <SiteFooter pub={pub} />
    </div>
  );
}

